import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from "axios";
import './index.css';
import App from './App';

const AUTH_STORAGE_KEYS = [
  "authToken",
  "token",
  "authTokenExpiresAt",
  "usuarioLogado",
  "lastActivityAt",
];

const INACTIVITY_LIMIT_MS = 4 * 60 * 60 * 1000;

const getAuthToken = () => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token");
};

const getTokenExpiresAt = () => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem("authTokenExpiresAt") || sessionStorage.getItem("authTokenExpiresAt");
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
};

const getLastActivityAt = () => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem("lastActivityAt") || sessionStorage.getItem("lastActivityAt");
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
};

const touchActivity = () => {
  if (typeof window === "undefined") return;
  localStorage.setItem("lastActivityAt", String(Date.now()));
  sessionStorage.setItem("lastActivityAt", String(Date.now()));
};

let logoutInProgress = false;
const forceLogout = () => {
  if (typeof window === "undefined" || logoutInProgress) return;
  logoutInProgress = true;

  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  delete axios.defaults.headers.common.Authorization;

  if (window.location.pathname !== "/") {
    window.location.replace("/");
    return;
  }

  window.location.reload();
};

const hasActiveSession = () => Boolean(getAuthToken());

const hasTokenExpired = () => {
  if (!hasActiveSession()) return false;
  const expiresAt = getTokenExpiresAt();
  return Number.isFinite(expiresAt) && Date.now() >= expiresAt;
};

const hasInactiveSession = () => {
  if (!hasActiveSession()) return false;
  const lastActivityAt = getLastActivityAt();
  return Number.isFinite(lastActivityAt) && Date.now() - lastActivityAt >= INACTIVITY_LIMIT_MS;
};

const token = getAuthToken();
if (token) {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  touchActivity();
}

axios.interceptors.request.use((config) => {
  if (hasTokenExpired() || hasInactiveSession()) {
    forceLogout();
    return Promise.reject(new Error("Sessao expirada"));
  }

  const t = getAuthToken();
  if (!t) return config;

  touchActivity();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      Authorization: `Bearer ${t}`,
    },
  };
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status || 0);
    const msg = String(error?.response?.data?.error || error?.response?.data?.message || "").toLowerCase();
    const unauthorized = status === 401;
    const tokenRelated = msg.includes("token") || msg.includes("jwt") || msg.includes("expir");

    if (unauthorized && tokenRelated) {
      forceLogout();
    }

    return Promise.reject(error);
  }
);

const shouldAttachAuthToFetch = (input) => {
  if (typeof window === "undefined") return false;
  const url = typeof input === "string" ? input : String(input?.url || "");
  if (!url) return false;
  if (url.startsWith("/")) return true;

  const currentOrigin = window.location.origin;
  const devApiOrigin = currentOrigin.includes(":3000")
    ? currentOrigin.replace(":3000", ":3001")
    : `${currentOrigin}/api`;

  return url.startsWith(currentOrigin) || url.startsWith(devApiOrigin);
};

if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    if (hasTokenExpired() || hasInactiveSession()) {
      forceLogout();
      throw new Error("Sessao expirada");
    }

    const tokenValue = getAuthToken();
    let nextInit = init;

    if (tokenValue) touchActivity();

    if (tokenValue && shouldAttachAuthToFetch(input)) {
      const headers = new Headers(init?.headers || {});
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${tokenValue}`);
      }
      nextInit = {
        ...init,
        headers,
      };
    }

    const response = await originalFetch(input, nextInit);
    if (response.status === 401) {
      const cloned = response.clone();
      const bodyText = await cloned.text().catch(() => "");
      const msg = String(bodyText || "").toLowerCase();
      if (msg.includes("token") || msg.includes("expir")) {
        forceLogout();
      }
    }

    return response;
  };
}

if (typeof window !== "undefined") {
  const activityEvents = ["click", "keydown", "mousemove", "scroll", "touchstart"];
  activityEvents.forEach((eventName) => {
    window.addEventListener(eventName, () => {
      if (getAuthToken()) touchActivity();
    }, { passive: true });
  });

  setInterval(() => {
    if (hasTokenExpired() || hasInactiveSession()) forceLogout();
  }, 15000);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);



